// ================================================================
// 📋 SMART TABLE - Tabela Inteligente para Relatórios
// ================================================================

import React, { useState, useMemo } from 'react';
import { Table, Form, Pagination, Badge } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import CurrencyDisplay from './CurrencyDisplay';

/**
 * 📋 SmartTable - Tabela com ordenação, busca e paginação
 * 
 * @param {Object} props
 * @param {Array} props.data - Dados da tabela
 * @param {Array} props.columns - Configuração das colunas
 * @param {boolean} props.loading - Estado de carregamento
 * @param {boolean} props.searchable - Habilitar busca
 * @param {number} props.itemsPerPage - Itens por página
 * @param {string} props.emptyMessage - Mensagem quando vazio
 */
const SmartTable = ({ 
  data = [], 
  columns = [],
  loading = false,
  searchable = true,
  itemsPerPage = 10,
  emptyMessage = 'Nenhum dado encontrado'
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);

  /**
   * Filtra dados baseado na busca
   */
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    return data.filter(item => {
      return columns.some(col => {
        const value = item[col.key];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(searchTerm.toLowerCase());
      });
    });
  }, [data, searchTerm, columns]);

  /**
   * Ordena dados
   */
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  /**
   * Pagina dados
   */
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, itemsPerPage]);

  /**
   * Total de páginas
   */
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  /**
   * Ordena coluna
   */
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  /**
   * Formata valor da célula
   */
  const formatCell = (value, column, row) => {
    if (value === null || value === undefined) return '-';

    // Custom render
    if (column.render) {
      return column.render(value, row);
    }

    // Formatação baseada no tipo
    switch (column.format) {
      case 'currency':
        return <CurrencyDisplay value={value} />;
      case 'percent':
        return `${Number(value).toFixed(2)}%`;
      case 'date':
        return new Date(value).toLocaleDateString('pt-BR');
      case 'datetime':
        return new Date(value).toLocaleString('pt-BR');
      case 'badge':
        return <Badge bg={column.badgeColor || 'primary'}>{value}</Badge>;
      default:
        return value;
    }
  };

  /**
   * Renderiza ícone de ordenação
   */
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <Icon icon="mdi:unfold-more-horizontal" style={{ fontSize: '16px', color: 'var(--dark-text-tertiary, #adb5bd)' }} />;
    }
    return sortConfig.direction === 'asc' 
      ? <Icon icon="mdi:arrow-up" style={{ fontSize: '16px', color: 'var(--dark-text-primary, #6c757d)' }} />
      : <Icon icon="mdi:arrow-down" style={{ fontSize: '16px', color: 'var(--dark-text-primary, #6c757d)' }} />;
  };

  /**
   * Renderiza paginação
   */
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '20px'
      }}>
        <span style={{ fontSize: '14px', color: 'var(--dark-text-secondary, #6c757d)' }}>
          Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, sortedData.length)} de {sortedData.length} resultados
        </span>
        <Pagination style={{ margin: 0 }}>
          <Pagination.First 
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          />
          <Pagination.Prev
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          />
          {startPage > 1 && <Pagination.Ellipsis disabled />}
          {pages}
          {endPage < totalPages && <Pagination.Ellipsis disabled />}
          <Pagination.Next
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          />
          <Pagination.Last
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
    );
  };

  return (
    <div style={{
      backgroundColor: 'var(--card-bg)',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      // border: '1px solid var(--dark-border, #e0e0e0)'
    }}>
      {/* Busca */}
      {searchable && (
        <Form.Group className="mb-3">
          <Form.Control
            type="text"
            placeholder="🔍 Buscar..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Resetar para primeira página ao buscar
            }}
            style={{
              borderRadius: '8px',
              border: '2px solid var(--dark-border, #e9ecef)',
              padding: '10px 16px',
              fontSize: '14px',
              backgroundColor: 'var(--dark-input-bg, #fff)',
              color: 'var(--dark-text-primary, #212529)'
            }}
          />
        </Form.Group>
      )}

      {/* Tabela */}
      <div style={{ overflowX: 'auto' }}>
        <Table hover responsive style={{ marginBottom: 0 }}>
          <thead style={{
            backgroundColor: 'var(--dark-table-header-bg, #f8f9fa)',
            borderBottom: '2px solid var(--dark-border, #dee2e6)'
          }}>
            <tr>
              {columns.map(column => (
                <th
                  key={column.key}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                  style={{
                    cursor: column.sortable !== false ? 'pointer' : 'default',
                    padding: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--dark-text-primary, #495057)',
                    whiteSpace: 'nowrap',
                    userSelect: 'none'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {column.label}
                    {column.sortable !== false && renderSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: 'center', padding: '40px' }}>
                  <Icon 
                    icon="mdi:loading" 
                    className="spin" 
                    style={{ fontSize: '32px', color: '#007bff' }} 
                  />
                  <div style={{ marginTop: '12px', color: 'var(--dark-text-secondary, #6c757d)' }}>
                    Carregando dados...
                  </div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: 'center', padding: '40px' }}>
                  <Icon 
                    icon="mdi:database-off" 
                    style={{ fontSize: '48px', color: 'var(--dark-text-tertiary, #dee2e6)' }} 
                  />
                  <div style={{ marginTop: '12px', color: 'var(--dark-text-secondary, #6c757d)' }}>
                    {emptyMessage}
                  </div>
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map(column => (
                    <td 
                      key={column.key}
                      style={{
                        padding: '16px',
                        fontSize: '14px',
                        color: 'var(--dark-text-primary, #212529)',
                        verticalAlign: 'middle',
                        borderBottom: '1px solid var(--dark-border, #f0f0f0)'
                      }}
                    >
                      {formatCell(row[column.key], column, row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Paginação */}
      {!loading && renderPagination()}
    </div>
  );
};

export default SmartTable;

